import { Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CalistDataService } from 'src/app/shared/store/features/calist-data/calist-data.service';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { ProfileFormComponent } from '../components/profile-form/profile-form.component';
import { catchError, finalize, map, of, tap } from 'rxjs';
import { CreateCertificateProfileRequestDto } from 'src/app/shared/services/api/dto/create-certificate-profile-request-dto';
import { ApiService } from 'src/app/shared/services/api/api.service';
import { PageService } from 'src/app/shared/page/service/page.service';
import { Page } from 'src/app/shared/page/page.config';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';

@Component({
  selector: 'app-create-profile-page',
  imports: [SpinnerComponent, ProfileFormComponent],
  templateUrl: './create-profile-page.component.html',
  styleUrl: './create-profile-page.component.scss',
})
export class CreateProfilePageComponent implements OnDestroy {
  private calistDataService = inject(CalistDataService);
  private apiService = inject(ApiService);
  private destroyRef = inject(DestroyRef);
  private pageService = inject(PageService);
  private toastrService = inject(ToastrService);

  calistLoading = toSignal(this.calistDataService.isLoading$);
  certificationAuthorities = toSignal(
    this.calistDataService.data$.pipe(map(calist => calist ?? [])),
    { initialValue: [] },
  );

  constructor() {
    this.calistDataService.ensure();
  }

  ngOnDestroy() {
    this.calistDataService.clear();
  }

  isCreating = false;

  onCreate(dto: CreateCertificateProfileRequestDto) {
    this.isCreating = true;
    this.apiService
      .createProfile(dto)
      .pipe(
        tap(() => {
          this.onSuccess();
        }),
        catchError(error => {
          this.onFail(error);
          return of();
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isCreating = false;
        }),
      )
      .subscribe();
  }

  onCancel() {
    this.pageService.goToPage(Page.ProfileList);
  }

  labels = {
    toasts: {
      created: 'Profile created successfully',
      fail: 'Failed to create profile',
    },
  };

  private onSuccess() {
    this.toastrService.success(this.labels.toasts.created);
    this.pageService.goToPage(Page.ProfileList);
  }

  private onFail(error: unknown) {
    console.error('Profile creation error', error);
    const defaultToast = this.labels.toasts.fail;
    const beError = extractErrorMessage(error);
    this.toastrService.error(beError || defaultToast);
  }
}
